/**
 * Iter208.81 — Shim per far girare i template pubblici dentro un modal di preview
 * senza nidificare un secondo <Router> (vietato in react-router-dom v7).
 *
 * Quando `PreviewNavContext` è fornito (dal modal), gli hook e i Link usano
 * quel contesto (state in-memory). Fuori dal modal, si comportano esattamente
 * come i hook nativi di react-router (fallback trasparente).
 *
 * I template importano:
 *    import { useSiteLocation, useSiteNavigate, SitePreviewLink as Link } from "@/lib/previewNav";
 * e usano `useSiteLocation()` / `useSiteNavigate()` al posto di `useLocation` /
 * `useNavigate`. L'oggetto `Link` è un drop-in di `<Link>` react-router.
 */
import React, { createContext, useContext } from "react";
import { Link as RRLink, useLocation, useNavigate } from "react-router-dom";

export const PreviewNavContext = createContext(null);

export function useSiteLocation() {
  const preview = useContext(PreviewNavContext);
  const rrLoc = useLocation();
  if (preview) return { pathname: preview.pathname, search: "", hash: "", state: null, key: "preview" };
  return rrLoc;
}

export function useSiteNavigate() {
  const preview = useContext(PreviewNavContext);
  const rrNav = useNavigate();
  if (preview) return preview.navigate;
  return rrNav;
}

/**
 * Drop-in per <Link>. In modalità preview intercetta i click e aggiorna lo
 * stato in-memory del modal invece di cambiare l'URL del browser.
 */
export function SitePreviewLink({ to, onClick, children, ...rest }) {
  const preview = useContext(PreviewNavContext);
  if (preview) {
    const href = typeof to === "string" ? to : "#";
    const handle = (e) => {
      onClick?.(e);
      if (e.defaultPrevented) return;
      // Solo click "normali" (no cmd/ctrl/nuova scheda)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      e.preventDefault();
      preview.navigate(href);
    };
    return <a href={href} onClick={handle} {...rest}>{children}</a>;
  }
  return <RRLink to={to} onClick={onClick} {...rest}>{children}</RRLink>;
}
